import { Trans, useLingui } from "@lingui/react/macro";
import { useFocusTrap } from "@offstreetparking/design-system/hooks";
import { ExternalLinkIcon } from "@offstreetparking/design-system/icons/ExternalLinkIcon";
import { Link, Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import { ELEMENT_ID as BOTTOM_BAR_ELEMENT_ID } from "@offstreetparking/portal-flow/components/layouts/BottomBar";
import { useElementPresent } from "@offstreetparking/portal-flow/hooks/useElementPresent";
import { PRIVACY_URL, TERMS_URL } from "@offstreetparking/portal-flow/lib/legal-urls";
import { buildSupportUrl } from "@offstreetparking/portal-flow/utils/support-url";
import NextLink from "next/link";
import { type ParkerContextValue, useParker } from "~/components/providers/AuthProvider";
import { useCompany } from "~/components/providers/CompanyProvider";
import { LanguageChanger } from "~/components/providers/TranslationsProvider";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useLogoutRedirect } from "~/hooks/useLogoutRedirect";
import { routes } from "~/routes";
import { TopBarUserDetails } from "../TopBarUserDropdown";
export type MobileNavProps = {
	isVisible: boolean;
	eventId?: string;
	locationId?: string;
	codeCheckHref: string;
} & (
	| Omit<
			Extract<
				ParkerContextValue,
				{
					isLoggedIn: true;
				}
			>,
			"refreshAuth"
	  >
	| {
			isLoggedIn: false;
	  }
);
export const MobileNav = ({ isVisible, eventId, locationId, codeCheckHref, ...parkerContext }: MobileNavProps) => {
	const { t } = useLingui();
	const logoutPath = useLogoutRedirect();
	const supportUrl = buildSupportUrl({ locationId, eventId });
	const opensInNewTab = t`(opens in a new tab)`;
	const { containerRef, handleKeyDown } = useFocusTrap({
		enabled: isVisible,
	});
	const isLoggedIn = parkerContext?.isLoggedIn;
	const { languageSwitcherFlag } = useFeatureFlags();
	const { globalParkerFlag } = useParker();
	const { isFromSubdomain } = useCompany();
	const { isElementVisible } = useElementPresent({
		id: BOTTOM_BAR_ELEMENT_ID,
	});
	const showPortalNavLinks = isLoggedIn && !isElementVisible;
	return (
		<nav
			ref={containerRef}
			id="mobile-nav-menu"
			aria-label={t`Main navigation`}
			inert={!isVisible}
			onKeyDown={handleKeyDown}
			className={classNames(
				"fixed max-h-screen w-full overflow-y-scroll overscroll-contain bg-white transition-all duration-500",
				isVisible ? "top-0 pt-16" : "-top-full mt-0",
			)}
		>
			{parkerContext.isLoggedIn && (
				<TopBarUserDetails
					displayName={parkerContext.displayName}
					firstName={parkerContext.parker.firstName}
					lastName={parkerContext.parker.lastName}
					email={parkerContext.parker.email}
				/>
			)}
			<div className="flex w-full flex-col items-start">
				{showPortalNavLinks && (
					<>
						<NextLink
							prefetch={false}
							className="w-full border-neutral-faded border-b p-4 text-left"
							href={routes.permits()}
						>
							<Text size="md">{t`Permits`}</Text>
						</NextLink>
						<NextLink
							prefetch={false}
							className="w-full border-neutral-faded border-b p-4 text-left"
							href={routes.transactions()}
						>
							<Text size="md">{t`Transactions`}</Text>
						</NextLink>
					</>
				)}
				<NextLink
					prefetch={false}
					className="w-full border-neutral-faded border-b p-4 text-left"
					href={routes.home()}
				>
					<Text size="md">
						{t({
							message: "Park",
							context: "nav",
							comment:
								"Top-nav destination label for the parking section; a noun, not the submit action.",
						})}
					</Text>
				</NextLink>
				<NextLink
					prefetch={false}
					className="w-full border-neutral-faded border-b p-4 text-left"
					href={codeCheckHref}
				>
					<Text size="md">{t`Code Check`}</Text>
				</NextLink>
				<NextLink
					prefetch={false}
					className="w-full border-neutral-faded border-b p-4 text-left"
					href={routes.sessionLookup({
						eventId,
					})}
				>
					<Text size="md">
						{t({
							message: "Session Lookup",
							comment:
								"Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.",
						})}
					</Text>
				</NextLink>
				{showPortalNavLinks && (
					<NextLink
						prefetch={false}
						className="w-full border-neutral-faded border-b p-4 text-left"
						href={routes.account()}
					>
						<Text size="md">{t`Account Settings`}</Text>
					</NextLink>
				)}
				<NextLink
					prefetch={false}
					className="w-full border-neutral-faded border-b p-4 text-left"
					href={supportUrl}
					target="_blank"
					aria-label={`${t`Help`} ${opensInNewTab}`}
				>
					<Text size="md">
						{t`Help`} <ExternalLinkIcon className="mb-0.5 inline h-3 w-3" />
					</Text>
				</NextLink>
				{(isFromSubdomain || globalParkerFlag) && !isLoggedIn && (
					<NextLink
						prefetch={false}
						className="w-full border-neutral-faded border-b p-4 text-left"
						href={routes.login()}
					>
						<Text size="md">
							{t({
								message: "Login",
								comment:
									"One-word nav label for the sign-in action, not a username field. Links to the login page.",
							})}
						</Text>
					</NextLink>
				)}
				{isLoggedIn && (
					<NextLink
						className="w-full border-neutral-faded border-b p-4 text-left"
						href={logoutPath}
						prefetch={false}
					>
						<Text size="md">{t`Logout`}</Text>
					</NextLink>
				)}
				{languageSwitcherFlag && (
					<div className="w-full border-neutral-faded border-b p-4 text-left">
						<LanguageChanger />
					</div>
				)}
			</div>

			<div className="flex flex-col items-center py-8">
				<Text size="sm">
					<Trans>
						Powered by{" "}
						<Link
							href="https://joinoffstreet.com"
							isExternal
							size="sm"
							intent="secondary"
							aria-label={`Offstreet ${opensInNewTab}`}
						>
							Offstreet
						</Link>
						.
					</Trans>
				</Text>
				<div className="mt-4 flex gap-8">
					<Link
						href={TERMS_URL}
						isExternal
						size="sm"
						intent="secondary"
						aria-label={`${t`Terms of Service`} ${opensInNewTab}`}
					>
						{t`Terms of Service`}
					</Link>
					<Link
						href={PRIVACY_URL}
						isExternal
						size="sm"
						intent="secondary"
						aria-label={`${t`Privacy Policy`} ${opensInNewTab}`}
					>
						{t`Privacy Policy`}
					</Link>
				</div>
				<Text size="md" className="mt-8">
					<Trans>
						Thanks for <span className="text-primary">parking with us!</span>
					</Trans>
				</Text>
			</div>
		</nav>
	);
};
